import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Clock } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { getHeroImage } from "@/data/blogImages";
import { fetchBlogPosts, fetchBlogCategories } from "@/lib/blog-api";

export default function Blog() {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([{ slug: "all", label: "All posts" }]);
  const [loading, setLoading] = useState(true);

  // One-time category fetch — small + rarely changes.
  useEffect(() => {
    let cancelled = false;
    fetchBlogCategories()
      .then((c) => {
        if (!cancelled && c.length) setCategories(c);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Refetch when filters change. Debounce the search input only — initial mount
  // and category changes fire immediately so the page never flashes empty state
  // longer than the network round trip requires.
  useEffect(() => {
    let cancelled = false;
    const delay = query ? 200 : 0;
    const handle = setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      fetchBlogPosts({ category, q: query })
        .then((list) => {
          if (!cancelled) {
            setPosts(list);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPosts([]);
            setLoading(false);
          }
        });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [category, query]);

  const featured = useMemo(() => posts[0], [posts]);
  const rest = useMemo(() => posts.slice(1), [posts]);

  return (
    <>
      <Seo
        title="Fintech Blog"
        description="Deep, opinionated writing on fintech infrastructure, personal finance methods, GDPR and AML compliance, embedded finance, and Open Banking. Written by the AtlasForge Financial team."
        path="/blog"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "AtlasForge Financial Blog",
            "url": "https://atlasforgefinancial.com/blog",
            "description":
              "Fintech infrastructure, personal finance methods, compliance, and industry analysis from AtlasForge Financial.",
            "publisher": {
              "@type": "Organization",
              "name": "AtlasForge Financial",
              "url": "https://atlasforgefinancial.com/",
            },
            "blogPost": posts.map((p) => ({
              "@type": "BlogPosting",
              "headline": p.title,
              "url": `https://atlasforgefinancial.com/blog/${p.slug}`,
              "datePublished": p.published_at,
              "author": { "@type": "Organization", "name": p.author },
            })),
          },
        ]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-20 pb-14 md:pt-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">AtlasForge Journal</p>
          <h1
            data-testid="blog-title"
            className="font-display mt-3 text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] text-[#0A1128] max-w-4xl"
          >
            Fintech, written for people who ship it.
          </h1>
          <p className="mt-5 text-[17px] text-slate-600 max-w-2xl">
            Guides on personal finance, banking APIs, compliance, and the
            infrastructure behind modern money — from the AtlasForge team.
          </p>

          <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search posts…"
                data-testid="blog-search"
                className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-[14px] outline-none focus:border-[#0A1128] focus:ring-2 focus:ring-[#0A1128]/10 transition"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="blog-categories">
              {categories.map((c) => {
                const active = category === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => setCategory(c.slug)}
                    data-testid={`blog-category-${c.slug}`}
                    className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-medium border transition-colors ${
                      active
                        ? "bg-[#0A1128] text-white border-[#0A1128]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#0A1128]"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-24 md:pb-28">
          {loading && posts.length === 0 ? (
            <div
              data-testid="blog-loading"
              className="flex items-center justify-center py-24"
            >
              <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#0A1128] animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-slate-500 py-16" data-testid="blog-empty">
              No posts match your search.
            </p>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid={`blog-featured-${featured.slug}`}
                  className="group grid lg:grid-cols-12 gap-8 rounded-3xl border border-slate-200 bg-white p-8 md:p-10 mb-12 hover:-translate-y-1 transition-transform af-shadow-card"
                >
                  <div className="lg:col-span-4 order-2 lg:order-1">
                    {getHeroImage(featured.slug) ? (
                      <img
                        src={getHeroImage(featured.slug)}
                        alt={featured.title}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover rounded-2xl"
                      />
                    ) : (
                      <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-[#F5F1EB] via-[#EAF6EF] to-[#EFF6FF] flex items-center justify-center text-6xl">
                        {featured.hero_emoji || "📰"}
                      </div>
                    )}
                  </div>
                  <div className="lg:col-span-8 order-1 lg:order-2">
                    <div className="flex items-center gap-3 text-[12px] text-slate-500">
                      <span className="uppercase tracking-[0.18em]">{featured.category}</span>
                      <span>·</span>
                      <span>{new Date(featured.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{featured.read_minutes} min read</span>
                    </div>
                    <h2 className="font-display text-[26px] md:text-[32px] leading-[1.1] text-[#0A1128] mt-3">
                      {featured.title}
                    </h2>
                    <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{featured.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#0A1128] group-hover:gap-2.5 transition-all">
                      Read the post <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
                {rest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 transition-transform af-shadow-card"
                  >
                    {getHeroImage(p.slug) ? (
                      <img
                        src={getHeroImage(p.slug)}
                        alt={p.title}
                        loading="lazy"
                        className="aspect-[16/9] w-full object-cover rounded-xl mb-5"
                      />
                    ) : (
                      <div className="aspect-[16/9] rounded-xl bg-gradient-to-br from-[#F5F1EB] via-[#EAF6EF] to-[#EFF6FF] flex items-center justify-center text-4xl mb-5">
                        {p.hero_emoji || "📰"}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[11.5px] text-slate-500 uppercase tracking-[0.14em]">
                      <span>{p.category}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1 normal-case tracking-normal"><Clock className="h-3 w-3" />{p.read_minutes} min</span>
                    </div>
                    <h3 className="font-display text-[20px] leading-[1.15] text-[#0A1128] mt-2 group-hover:text-[#1A4232] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1128] group-hover:gap-2.5 transition-all">
                      Read <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
